// src/contexts/AppStateContext.jsx
import React, { createContext, useContext, useState, useMemo, useCallback, useEffect } from 'react';
import { useConfig } from './ConfigContext';
import { useRetailerInitialization } from '../hooks/init/useRetailerInitialization';
import { useRetailerNavigation } from '../hooks/init/useRetailerNavigation';
import { useAppStateGates } from '../hooks/init/useAppStateGates';

const AppStateContext = createContext(null);

export const useAppState = () => {
    const context = useContext(AppStateContext);
    if (!context) {
        throw new Error('useAppState must be used within AppStateProvider');
    }
    return context;
};

export const AppStateProvider = ({ children }) => {
    const {
        locationGateConfig,
        isSingleRetailer,
        singleRetailerId,
        retailers,
        nonHeadlessRetailers,
        getRetailerById,
        getRetailerBySlug,
        shortcodeData,
        allRetailersConfig,
    } = useConfig();

    // Core app state
    const [fallbackEnabled, setFallbackEnabled] = useState(false);

    // UI state
    const [sidebarToggle, setSidebarToggle] = useState(false);
    const [productPopupData, setProductPopupData] = useState(null);
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [locationRedirect, setLocationRedirect] = useState(false);

    // Cart rendering state
    const [shouldRenderCart, setShouldRenderCart] = useState(false);

    // Use custom hooks for initialization and navigation
    const {
        isInitialized,
        retailerId,
        setRetailerId,
        locationSelected,
        setLocationSelected,
        nonHeadlessRetailer,
        setNonHeadlessRetailer,
        nonHeadlessRetailerData,
        setNonHeadlessRetailerData,
    } = useRetailerInitialization({
        retailers,
        nonHeadlessRetailers,
        shortcodeData,
        isSingleRetailer,
        singleRetailerId,
        getRetailerBySlug,
    });

    const { updateRetailerId: navigationUpdateRetailerId } = useRetailerNavigation({
        isInitialized,
        retailerId,
        retailers,
        isSingleRetailer,
        getRetailerBySlug,
        allRetailersConfig,
    });

    const {
        ageGatePassed,
        needsAgeGate,
        needsLocationGate,
        gatesPassed,
        isAppReady,
        passAgeGate,
        resetGates,
    } = useAppStateGates({
        locationGateConfig,
        isSingleRetailer,
        locationSelected,
        retailerId,
        nonHeadlessRetailer,
        isInitialized,
    });

    // Determine cart rendering
    useEffect(() => {
        const hasValidShortcode = Object.values(shortcodeData).some(
            data => ['singleproduct', 'headlessproducts', 'headlessmenu', 'shopnavfull'].includes(data.shortcode)
        );
        setShouldRenderCart(!nonHeadlessRetailer && hasValidShortcode);
    }, [shortcodeData, nonHeadlessRetailer]);

    // Update fallback enabled when retailer changes
    useEffect(() => {
        if (retailerId && window.rangeHeadlessApi?.fallback?.[retailerId]) {
            setFallbackEnabled(window.rangeHeadlessApi.fallback[retailerId].enabled || false);
        }
    }, [retailerId]);

    // Computed values
    const currentRetailer = useMemo(() =>
        retailerId ? getRetailerById(retailerId) : null,
        [retailerId, getRetailerById]
    );

    // UI functions
    const openSidebar = useCallback(() => setSidebarToggle(true), []);
    const closeSidebar = useCallback(() => setSidebarToggle(false), []);
    const toggleSidebar = useCallback(() => setSidebarToggle(prev => !prev), []);

    const openProductPopup = useCallback((productData) => {
        setProductPopupData(productData);
    }, []);

    const closeProductPopup = useCallback(() => {
        setProductPopupData(null);
    }, []);

    // Wrapped updateRetailerId that includes location selection
    const updateRetailerId = useCallback((newRetailerId) => {
        navigationUpdateRetailerId(newRetailerId, setRetailerId);
        setLocationSelected(true);
    }, [navigationUpdateRetailerId, setRetailerId, setLocationSelected]);

    // Combined resetGates that also resets location selection
    const resetAllGates = useCallback(() => {
        resetGates();
        setLocationSelected(false);
    }, [resetGates, setLocationSelected]);

    const contextValue = useMemo(() => ({
        // Core app state
        isInitialized,
        isAppReady,
        fallbackEnabled,

        // Retailer state
        retailerId,
        setRetailerId: updateRetailerId,
        currentRetailer,
        nonHeadlessRetailer,
        setNonHeadlessRetailer,
        nonHeadlessRetailerData,
        setNonHeadlessRetailerData,

        // UI state
        sidebarToggle,
        setSidebarToggle,
        openSidebar,
        closeSidebar,
        toggleSidebar,
        productPopupData,
        setProductPopupData,
        openProductPopup,
        closeProductPopup,
        mobileMenuOpen,
        setMobileMenuOpen,
        shouldRenderCart,

        // Gate state
        ageGatePassed,
        locationSelected,
        locationRedirect,
        setLocationRedirect,
        needsAgeGate,
        needsLocationGate,
        gatesPassed,
        passAgeGate,
        resetGates: resetAllGates,
    }), [
        isInitialized,
        isAppReady,
        fallbackEnabled,
        retailerId,
        updateRetailerId,
        currentRetailer,
        nonHeadlessRetailer,
        nonHeadlessRetailerData,
        sidebarToggle,
        openSidebar,
        closeSidebar,
        toggleSidebar,
        productPopupData,
        openProductPopup,
        closeProductPopup,
        mobileMenuOpen,
        shouldRenderCart,
        ageGatePassed,
        locationSelected,
        locationRedirect,
        needsAgeGate,
        needsLocationGate,
        gatesPassed,
        passAgeGate,
        resetAllGates,
    ]);

    return (
        <AppStateContext.Provider value={contextValue}>
            {children}
        </AppStateContext.Provider>
    );
};
